// ante-react-kit/src/components/v2/AntAddressSearch/index.js
// Ô tìm địa chỉ kiểu app giao hàng. Nhà cung cấp do MÁY CHỦ chọn theo nước (Mapbox/Google),
// giao diện không biết và không cần biết là bên nào.
// Chọn 1 gợi ý thì máy chủ quy đổi sẵn ra mã hành chính, gọi lại onSelect với
// { entity_address, adm1_code/name, adm2_code/name, adm3_code/name, postal_code, lat, lng }.
//
// Ba điểm quan trọng:
//  1. Máy chủ chưa khai khoá thì component TỰ ẨN (trả null) — không hiện ô rồi báo lỗi.
//  2. Gửi kèm vị trí hiện tại (nếu người dùng cho phép) để gõ vài chữ là ra chỗ gần nhất.
//  3. Cùng một mã phiên cho mọi lần gõ tới lúc chọn xong — nhà cung cấp tính tiền 1 lần/phiên.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './index.scss';
import AntLocalization from '../../../utils/AntLocalization';
import AntAPI from '../../../utils/AntAPI';
import AntLocalStorage from '../../../utils/AntLocalStorage';
import { useAddressSearchEnabled } from './capability';

// Kit bọc response {error,code,data:<return>}; postActionWithData resolve nguyên envelope → bóc .data.
var unwrap = function (r) {
    return (r && r.data ? r.data : r) || {};
};

// Mã phiên: gom các lần gõ + lần lấy chi tiết thành 1 lượt tính tiền.
var newSessionToken = function () {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

export default function AntAddressSearch(props) {
    var country = useMemo(function () {
        return (props.country || AntLocalStorage.getBusinessSetting('Sys_BaseCountry') || 'VN');
    }, [props.country]);
    var language = useMemo(function () {
        return (props.language || AntLocalStorage.getBusinessSetting('Sys_BaseLanguage') || 'vi');
    }, [props.language]);
    var API = useMemo(function () { return new AntAPI('sys', 'SysAddress'); }, []);

    var enabled = useAddressSearchEnabled(country);

    var [query, setQuery] = useState('');
    var [suggestions, setSuggestions] = useState([]);
    var [loading, setLoading] = useState(false);
    var [open, setOpen] = useState(false);
    var timerRef = useRef(null);
    var reqSeq = useRef(0);
    var blurTimer = useRef(null);
    var sessionRef = useRef(newSessionToken());
    var geoRef = useRef(null); // vị trí hiện tại {lat,lng} — có thì gợi ý ưu tiên chỗ gần

    // Xin vị trí MỘT LẦN khi ô được bật. Người dùng từ chối cũng không sao, chỉ mất phần ưu tiên gần.
    useEffect(
        function () {
            if (!enabled || geoRef.current) return;
            if (typeof navigator === 'undefined' || !navigator.geolocation) return;
            var alive = true;
            navigator.geolocation.getCurrentPosition(
                function (pos) {
                    if (alive && pos && pos.coords) geoRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                },
                function () {
                    /* từ chối hoặc hết giờ — bỏ qua */
                },
                { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
            );
            return function () {
                alive = false;
            };
        },
        [enabled]
    );

    var runSearch = useCallback(function (text) {
        var seq = ++reqSeq.current;
        setLoading(true);
        var g = geoRef.current || {};
        API.postActionWithData('actionPlaceAutocomplete', {
            input: text,
            country: country,
            language: language,
            session_token: sessionRef.current,
            lat: g.lat,
            lng: g.lng
        })
            .then(function (res) {
                if (seq !== reqSeq.current) return; // bỏ kết quả cũ (đua bàn phím)
                setSuggestions(unwrap(res).predictions || []);
                setLoading(false);
                setOpen(true);
            })
            .catch(function () {
                if (seq !== reqSeq.current) return;
                setLoading(false);
            });
    }, [API, country, language]);

    var onChange = useCallback(function (e) {
        var text = e.target.value;
        setQuery(text);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (!text || text.trim().length < 2) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        timerRef.current = setTimeout(function () { runSearch(text.trim()); }, 350);
    }, [runSearch]);

    var onPick = useCallback(function (item) {
        setOpen(false);
        setLoading(true);
        API.postActionWithData('actionPlaceDetails', {
            place_id: item.place_id,
            country: country,
            language: language,
            session_token: sessionRef.current
        })
            .then(function (res) {
                setLoading(false);
                sessionRef.current = newSessionToken(); // chọn xong là đóng phiên, lần tìm sau tính phiên mới
                var d = unwrap(res);
                if (!d || d.error || d.disabled) return; // vẫn nhận cả khi thiếu số nhà (chọn cấp phường/thành phố)
                setQuery(d.formatted_address || d.entity_address || item.description || '');
                if (props.onSelect) props.onSelect(d);
            })
            .catch(function () { setLoading(false); });
    }, [API, country, language, props]);

    var placeholder = AntLocalization.translateText(props.placeholder || { vi: 'Tìm địa chỉ giao hàng…', en: 'Search delivery address…', fr: 'Chercher une adresse…', es: 'Buscar dirección…', id: 'Cari alamat kirim…', ja: '配送先を検索…', zh: '搜索收货地址…', ko: '배송 주소 검색…', hi: 'डिलीवरी पता खोजें…', ar: 'ابحث عن عنوان…' });

    // Máy chủ chưa khai khoá (hoặc đang hỏi) → ẩn hẳn, màn hình chỉ còn cách chọn theo cấp.
    if (!enabled) return null;

    return (
        <div className="v2-address-search">
            <div className="v2-address-search__box">
                <span className="v2-address-search__icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                </span>
                <input
                    type="text"
                    className="v2-address-search__input"
                    value={query}
                    onChange={onChange}
                    onFocus={function () { if (blurTimer.current) clearTimeout(blurTimer.current); if (suggestions.length) setOpen(true); }}
                    onBlur={function () { blurTimer.current = setTimeout(function () { setOpen(false); }, 180); }}
                    placeholder={placeholder}
                    disabled={props.disabled}
                    autoComplete="off"
                />
                {loading && <span className="v2-address-search__spinner" />}
                {query && !loading && (
                    <button type="button" className="v2-address-search__clear" onMouseDown={function (e) { e.preventDefault(); setQuery(''); setSuggestions([]); setOpen(false); }}>×</button>
                )}
            </div>
            {open && suggestions.length > 0 && (
                <div className="v2-address-search__list">
                    {suggestions.map(function (item) {
                        return (
                            <div key={item.place_id} className="v2-address-search__row" onMouseDown={function (e) { e.preventDefault(); onPick(item); }}>
                                <span className="v2-address-search__pin">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                                    </svg>
                                </span>
                                <span className="v2-address-search__desc">{item.description}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
